import { useUpdateRef } from '@/hooks/gHooks';
import { insertTextAtCursor } from '@/utils';
import {
  FC,
  KeyboardEvent,
  CSSProperties,
  ForwardRefRenderFunction,
  ForwardedRef,
  Ref,
} from 'react';
import { styled } from 'styled-components';
import { useLang } from '@/locales/useLang';
import { callFromExternal } from '@/utils';

interface IInputProps {
  onChange: (content: string) => void;
  onSend?: (content: string) => void;
  style?: CSSProperties;
  maxWord?: number;
  isFullScreen?: boolean;
  onFocus?: (isFocus: boolean) => void;
}

export interface IInputRef {
  inputRef: HTMLDivElement;
  contentText: React.RefObject<string>;
  resetContent: () => void;
}

const InputWrap = styled.div<{
  $isFullScreen: boolean;
  $color: string;
  $fontSize: string;
  $lineHeight: string;
}>`
  width: ${(prpos) => (prpos.$isFullScreen ? '670rem' : '480rem')};
  height: ${(prpos) => (prpos.$isFullScreen ? 'calc(100% - 176rem)' : '100%')};
  position: ${(prpos) => (prpos.$isFullScreen ? 'fixed' : 'static')};

  overflow: auto;
  font-size: ${(props) => props.$fontSize};
  left: 0;
  right: 0;
  margin: 0 auto;
  color: ${(props) => props.$color};
  line-height: ${(props) => props.$lineHeight};

  ${(props) => {
    if (!props.$isFullScreen) {
      return 'max-height: 230rem; top: 176rem';
    }
  }}
`;

const Input = forwardRef((props: IInputProps, ref: ForwardedRef<IInputRef>) => {
  const $t = useLang();
  const placeHolderText = $t('chat_session_status_12');

  const [placeHolder, setPlaceHolder] = useState(placeHolderText);
  const inputRef = useRef<HTMLDivElement>(null);
  const [contentText, updateContentText] = useUpdateRef('');

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        document.execCommand('insertLineBreak');
        e.preventDefault();
      } else {
        document.execCommand('insertLineBreak');
        e.preventDefault();
        // props.onSend(content);
      }
    }
  };

  /**
   * 重置输入框消息
   */
  const resetContent = () => {
    inputRef.current && (inputRef.current.innerHTML = '');
  };

  useImperativeHandle<IInputRef, any>(ref, () => ({
    inputRef: inputRef.current,
    contentText,
    resetContent,
  }));

  const handleFocus = () => {
    if (!contentText.current) {
      setPlaceHolder('');
    }

    props.onFocus?.(true);
  };

  const handleBlur = () => {
    if (!contentText.current) {
      setPlaceHolder(placeHolderText);
    }
    props.onFocus?.(false);
  };

  return (
    <InputWrap
      id="mobInput"
      onPaste={(e) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        insertTextAtCursor(text);

        const content = inputRef.current?.innerHTML;
        contentText.current = String(inputRef.current?.textContent);
        props.onChange(content!);
      }}
      ref={inputRef}
      $color={placeHolder ? callFromExternal() ? 'rgba(69, 72, 76, 1)' : '#EBEBEB' : callFromExternal() ? '#fff' : '#000'}
      $fontSize={placeHolder ? '20rem' : '28rem'}
      $lineHeight={placeHolder ? '2' : 'auto'}
      $isFullScreen={props.isFullScreen ?? false}
      contentEditable
      dangerouslySetInnerHTML={{ __html: placeHolder }}
      onCompositionStart={() => {}}
      onCompositionEnd={() => {}}
      onFocus={() => handleFocus()}
      onBlur={() => handleBlur()}
      onInput={() => {
        const content = inputRef.current?.innerHTML;
        contentText.current = String(inputRef.current?.textContent);
        props.onChange(content!);
      }}
      onKeyUp={(e) => {}}
      onKeyDown={(e) => {
        const c = inputRef.current?.textContent;
        if (c && c.length > props.maxWord!) {
          inputRef.current.textContent = c.substring(0, props.maxWord);

          const range = document.createRange();
          const selection = window.getSelection();

          range.selectNodeContents(inputRef.current!);
          range.collapse(false);
          selection?.removeAllRanges();
          selection?.addRange(range);

          return;
        }
        handleKeyDown(e);
      }}
    ></InputWrap>
  );
});

export default Input;

export const Mask: FC<{ show: boolean }> = ({ show }) => {
  return (
    <div
      className={`fixed left-0 top-0 w-full h-full bg-#F3F4F6 ${show ? 'block' : 'hidden'}`}
    ></div>
  );
};
